import React, { useEffect, useState, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

import UpdateStatus from './UpdateStatus';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  RadialLinearScale,
  Legend,
  BarElement,
  ArcElement,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut, Radar, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  RadialLinearScale,
  Legend,
  BarElement,
  ArcElement,
  Filler
);

const API_BASE_URL = 'https://greenyellow-nightingale-241896.hostingersite.com/api';

const EmployeePerformance = () => {
  // Core state
  const [employees, setEmployees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('completionRate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [chartType, setChartType] = useState('line');
  const [chartMetric, setChartMetric] = useState('completionRate');

  // Enhanced features state
  const [viewMode, setViewMode] = useState('table');
  const [filterBy, setFilterBy] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showTaskDetails, setShowTaskDetails] = useState({});
  const [exportType, setExportType] = useState('csv');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [performanceGoals, setPerformanceGoals] = useState({});
  const [performanceHistory, setPerformanceHistory] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [refreshIntervalId, setRefreshIntervalId] = useState(null);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [customMetrics, setCustomMetrics] = useState([]);
  const [dashboardLayout, setDashboardLayout] = useState('default');

  // Individual Employee Performance State
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeePerformanceView, setEmployeePerformanceView] = useState(false);
  const [employeeTasks, setEmployeeTasks] = useState([]);
  const [employeeChartType, setEmployeeChartType] = useState('bar');
  const [employeeChartMetric, setEmployeeChartMetric] = useState('completedTasks');

  const [alertThresholds] = useState({
    lowPerformance: 40,
    highLateTasks: 30,
    lowCompletionRate: 60
  });

  // Filter states
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedDepartmentEmployee, setSelectedDepartmentEmployee] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedTaskForUpdate, setSelectedTaskForUpdate] = useState(null);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const performanceChartRef = useRef(null);
  const analyticsRef = useRef(null);

  const authToken = localStorage.getItem('authToken')?.replace(/['\"]/g, '').trim();

  // Helper function
  const getMetricLabel = useCallback((metric) => {
    switch (metric) {
      case 'completedTasks': return 'Completed Tasks';
      case 'completionRate': return 'Completion Rate (%)';
      case 'onTimeRate': return 'On-Time Rate (%)';
      case 'performanceScore': return 'Performance Score';
      case 'taskCount': return 'Task Count';
      default: return 'Completed Tasks';
    }
  }, []);

  // Date formatter helper function (dd-mm-yyyy)
  const formatDate = useCallback((date) => {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }, []);

  // Company ID resolution
  const getCompanyId = useCallback(async () => {
    console.group('🔍 [DEBUG] getCompanyId() - Starting company ID resolution');

    const token = localStorage.getItem('authToken');

    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        console.log('📊 Decoded token structure:', decodedToken);

        if (decodedToken && decodedToken.role === 'company') {
          if (decodedToken.id) {
            console.log('Company role detected, using id as companyId:', decodedToken.id);
            console.groupEnd();
            return decodedToken.id;
          }
        } else {
          if (decodedToken && decodedToken.companyId) {
            console.log('✅ Found companyId directly in JWT:', decodedToken.companyId);
            console.groupEnd();
            return decodedToken.companyId;
          }
        }
      } catch (error) {
        console.error('❌ JWT decoding failed:', error);
      }
    }

    const storedCompanyId = localStorage.getItem('companyId');
    if (storedCompanyId) {
      console.log('✅ Found companyId in localStorage:', storedCompanyId);
      console.groupEnd();
      return storedCompanyId;
    }

    console.error('❌ Unable to resolve company ID');
    console.groupEnd();
    return null;
  }, []);

  // User info extraction
  const getUserInfo = useCallback(() => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return null;

      const decoded = jwtDecode(token);
      return {
        id: decoded.id || decoded.userId,
        role: decoded.role,
        companyId: decoded.companyId
      };
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }, []);

  // Fetch departments
  const fetchDepartments = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const companyId = await getCompanyId();
      if (!companyId) {
        throw new Error('Unable to determine company ID');
      }

      const response = await fetch(`${API_BASE_URL}/departments/company/${companyId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch departments: ${response.status}`);
      }

      const data = await response.json();
      const departmentList = Array.isArray(data) ? data : (data.data || data.departments || []);
      return departmentList;
    } catch (error) {
      console.error('Error fetching departments:', error);
      return [];
    }
  }, [getCompanyId]);

  const handleTaskClick = (task) => {
    setSelectedTaskForUpdate(task);
    setShowUpdateStatusModal(true);
  };

  const departmentEmployees = React.useMemo(() => {
    if (selectedDepartment === 'all') return [];

    console.log('⚙️ Filtering employees for department:', selectedDepartment);
    console.log('📊 Total employees:', employees.length);

    const filtered = employees.filter(emp => {
      if (emp.isActive === false) return false;
      if (Array.isArray(emp.department)) {
        const isInDepartment = emp.department.includes(selectedDepartment) ||
          emp.department.some(deptId => String(deptId) === String(selectedDepartment));
        if (isInDepartment) {
          console.log('✅ Match found:', emp.name || emp.teamMemberName);
        }
        return isInDepartment;
      }
      const empDeptId = typeof emp.department === 'string' ? emp.department :
        (emp.department?._id || emp.department?.id || emp.departmentId);
      const matches = empDeptId === selectedDepartment ||
        String(empDeptId) === String(selectedDepartment);
      if (matches) {
        console.log('✅ Match found:', emp.name || emp.teamMemberName);
      }
      return matches;
    });
    console.log(`✅ Found ${filtered.length} employees in selected department`);
    return filtered;
  }, [selectedDepartment, employees]);

  // API fetch functions
  const fetchTasksWithCreditPoints = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['\"]/g, '').trim();
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('🌐 Fetching tasks with credit points...');

      const response = await fetch(`${API_BASE_URL}/tasks/creditpoints/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch credit points tasks: ${response.status}`);
      }

      const data = await response.json();
      console.log('📦 Credit points tasks data received:', data);

      const taskList = Array.isArray(data) ? data : (data.data || data.tasks || []);
      return taskList;

    } catch (error) {
      console.error('Error fetching tasks with credit points:', error);
      throw error;
    }
  }, []);

  const fetchEmployeesWithCreditPoints = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['\"]/g, '').trim();
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('🌐 Fetching employees with credit points...');

      const response = await fetch(`${API_BASE_URL}/tasks/creditpoints/employees`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch employees with credit points: ${response.status}`);
      }

      const data = await response.json();
      console.log('📦 Employees with credit points data received:', data);

      const employeeList = Array.isArray(data) ? data : (data.data || data.employees || []);
      return employeeList.filter(emp => emp.isActive !== false);

    } catch (error) {
      console.error('Error fetching employees with credit points:', error);
      throw error;
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const companyId = await getCompanyId();
      if (!companyId) {
        throw new Error('Unable to determine company ID');
      }

      const token = localStorage.getItem('authToken')?.replace(/['\"]/g, '').trim();
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('🌐 Fetching employees for company:', companyId);

      const response = await fetch(`${API_BASE_URL}/employees/company/${companyId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch employees: ${response.status}`);
      }

      const data = await response.json();
      console.log('📦 Employees data received:', data);

      const employeeList = Array.isArray(data) ? data : (data.data || data.employees || []);
      return employeeList.filter(emp => emp.isActive !== false);

    } catch (error) {
      console.error('Error fetching employees:', error);
      throw error;
    }
  }, [getCompanyId]);

  const fetchRegularTasks = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['\"]/g, '').trim();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/task/tasks`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch regular tasks: ${response.status}`);
      }

      const data = await response.json();
      const taskList = Array.isArray(data) ? data : (data.data || data.tasks || []);
      return taskList;

    } catch (error) {
      console.error('Error fetching regular tasks:', error);
      throw error;
    }
  }, []);

  // Performance calculation based on task completion only
  const calculateIndividualPerformance = useCallback((employee, tasks) => {
    if (!employee || !tasks) {
      return {
        name: employee?.name || 'Unknown Employee',
        completedTasks: 0,
        onTimeTasks: 0,
        lateTasks: 0,
        totalAssignedTasks: 0,
        completionRate: 0,
        onTimeRate: 0,
        performanceScore: 0,
        taskDetails: [],
        monthlyPerformance: {}
      };
    }

    let completedTasks = 0;
    let onTimeTasks = 0;
    let lateTasks = 0;
    let totalAssignedTasks = tasks.length;
    const taskDetails = [];

    tasks.forEach(task => {
      const taskStatus = task.status ? task.status.toLowerCase().trim() : '';
      const isCompleted = taskStatus.includes('complete') ||
        taskStatus === 'completed' ||
        taskStatus === 'done' ||
        taskStatus === 'finished' ||
        taskStatus === 'closed' ||
        taskStatus.includes('finish');

      const isInProgress = taskStatus.includes('progress') ||
        taskStatus === 'in-progress' ||
        taskStatus === 'inprogress' ||
        taskStatus === 'ongoing' ||
        taskStatus === 'active' ||
        taskStatus === 'working';

      if (isCompleted) {
        completedTasks++;

        let endDate, completionDate;
        try {
          endDate = new Date(task.endDateTime);
          if (isNaN(endDate.getTime())) endDate = new Date();
        } catch (error) {
          endDate = new Date();
        }

        try {
          const completionTimeStamp = task.completionDateTime ||
            task.updatedAt ||
            task.completedAt ||
            task.dateCompleted ||
            Date.now();
          completionDate = new Date(completionTimeStamp);
          if (isNaN(completionDate.getTime())) completionDate = new Date();
        } catch (error) {
          completionDate = new Date();
        }

        const isOnTime = completionDate <= endDate;

        if (isOnTime) {
          onTimeTasks++;
        } else {
          lateTasks++;
        }

        taskDetails.push({
          title: task.title || 'Untitled Task',
          endDate: endDate,
          completionDate: completionDate,
          isOnTime: isOnTime,
          status: task.status,
          priority: task.priority || 'Medium'
        });
      } else if (isInProgress) {
        let endDate;
        try {
          endDate = new Date(task.endDateTime);
          if (isNaN(endDate.getTime())) endDate = new Date();
        } catch (error) {
          endDate = new Date();
        }

        taskDetails.push({
          title: task.title || 'Untitled Task',
          endDate: endDate,
          completionDate: null,
          isOnTime: null,
          status: task.status || 'In Progress',
          priority: task.priority || 'Medium'
        });
      } else {
        let endDate;
        try {
          endDate = new Date(task.endDateTime);
          if (isNaN(endDate.getTime())) endDate = new Date();
        } catch (error) {
          endDate = new Date();
        }

        taskDetails.push({
          title: task.title || 'Untitled Task',
          endDate: endDate,
          completionDate: null,
          isOnTime: null,
          status: task.status || 'Pending',
          priority: task.priority || 'Medium'
        });
      }
    });

    const completionRate = totalAssignedTasks > 0 ? (completedTasks / totalAssignedTasks) * 100 : 0;
    const onTimeRate = completedTasks > 0 ? (onTimeTasks / completedTasks) * 100 : 0;
    const performanceScore = completionRate;

    return {
      ...employee,
      completedTasks,
      onTimeTasks,
      lateTasks,
      totalAssignedTasks,
      completionRate,
      onTimeRate,
      performanceScore,
      taskDetails,
      monthlyPerformance: calculateMonthlyPerformance(taskDetails)
    };
  }, []);

  const calculateMonthlyPerformance = (taskDetails) => {
    const monthlyData = {};
    taskDetails.forEach(task => {
      if (task.completionDate) {
        const month = new Date(task.completionDate).toLocaleString('default', { month: 'short', year: 'numeric' });
        if (!monthlyData[month]) {
          monthlyData[month] = { completed: 0 };
        }
        monthlyData[month].completed += 1;
      }
    });
    return monthlyData;
  };

  // Main performance calculation
  const performanceData = React.useMemo(() => {
    if (!employees.length || !tasks.length) {
      console.log('❌ No data available:', { employees: employees.length, tasks: tasks.length });
      return [];
    }

    console.group('📊 Calculating performance data with all task statuses');
    console.log('📋 Input data:', { employees: employees.length, tasks: tasks.length });

    const performance = new Map();
    employees.forEach(emp => {
      const empId = emp._id || emp.id || emp.employeeId;
      const empName = emp.teamMemberName || emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unknown Employee';

      performance.set(empId, {
        id: empId,
        name: empName,
        completedTasks: 0,
        inProgressTasks: 0,
        pendingTasks: 0,
        onTimeTasks: 0,
        lateTasks: 0,
        totalAssignedTasks: 0,
        completionRate: 0,
        onTimeRate: 0,
        performanceScore: 0,
        taskDetails: []
      });
    });

    console.log('⚙️ Performance map initialized with', performance.size, 'employees');

    // Filter out recurring parent templates (same as Tasks.jsx logic)
    const filteredTasks = tasks.filter((task) => {
      const isParentTemplate = task.repeat && !task.isRecurringInstance;
      const isInstance = task.isRecurringInstance;
      const isNonRecurring = !task.repeat && !isInstance;
      // Only count non-recurring tasks or recurring instances, NOT parent templates
      return isNonRecurring || isInstance;
    });

    console.log('📋 Filtered tasks (excluding recurring parents):', {
      totalTasks: tasks.length,
      filteredTasks: filteredTasks.length,
      excluded: tasks.length - filteredTasks.length
    });

    filteredTasks.forEach((task, taskIndex) => {
      if (!task.assignedTo) {
        return;
      }

      let assignees = [];

      if (Array.isArray(task.assignedTo)) {
        assignees = task.assignedTo;
      } else if (typeof task.assignedTo === 'string') {
        assignees = [task.assignedTo];
      } else if (typeof task.assignedTo === 'object' && task.assignedTo._id) {
        assignees = [task.assignedTo._id];
      } else if (typeof task.assignedTo === 'object' && task.assignedTo.id) {
        assignees = [task.assignedTo.id];
      } else {
        assignees = [task.assignedTo];
      }

      assignees.forEach((assignee) => {
        let empId;
        if (typeof assignee === 'string') {
          empId = assignee;
        } else if (typeof assignee === 'object' && assignee._id) {
          empId = assignee._id;
        } else if (typeof assignee === 'object' && assignee.id) {
          empId = assignee.id;
        } else {
          empId = String(assignee);
        }

        let empPerf = performance.get(empId);

        if (!empPerf) {
          for (const [key, emp] of performance.entries()) {
            const empName = emp.name;
            if (key === empId ||
              key.includes(empId) ||
              empId.includes(key) ||
              key.toLowerCase() === empId.toLowerCase() ||
              empName === empId ||
              empName.toLowerCase() === empId.toLowerCase() ||
              (empId && empName && empName.toLowerCase().includes(empId.toLowerCase())) ||
              (empId && empName && empId.toLowerCase().includes(empName.toLowerCase()))) {
              empPerf = performance.get(key);
              break;
            }
          }

          if (!empPerf) return;
        }

        empPerf.totalAssignedTasks++;

        const taskStatus = task.status ? task.status.toLowerCase().trim() : '';

        // Use exact same normalization as Tasks.jsx for consistency
        const isCompleted = taskStatus === 'completed' ||
          taskStatus === 'late-complete' ||
          taskStatus === 're-complete' ||
          taskStatus === 're-late-complete';

        const isInProgress = taskStatus === 'in-progress' ||
          taskStatus === 'in-progress(overdue)' ||
          taskStatus === 're-in-progress' ||
          taskStatus === 're-in-progress(overdue)';

        const isReopened = taskStatus === 'reopened' ||
          taskStatus === 'reopened(overdue)';

        const isPending = taskStatus === 'pending';

        if (isCompleted) {
          empPerf.completedTasks++;

          let endDate, completionDate;

          try {
            endDate = new Date(task.endDateTime);
            if (isNaN(endDate.getTime())) {
              endDate = new Date();
            }
          } catch (error) {
            endDate = new Date();
          }

          try {
            const completionTimeStamp = task.completionDateTime ||
              task.updatedAt ||
              task.completedAt ||
              task.dateCompleted ||
              Date.now();
            completionDate = new Date(completionTimeStamp);
            if (isNaN(completionDate.getTime())) {
              completionDate = new Date();
            }
          } catch (error) {
            completionDate = new Date();
          }

          const isOnTime = completionDate <= endDate;

          if (isOnTime) {
            empPerf.onTimeTasks++;
          } else {
            empPerf.lateTasks++;
          }

          const taskDetail = {
            title: task.title || 'Untitled Task',
            endDate: endDate,
            completionDate: completionDate,
            isOnTime: isOnTime
          };

          empPerf.taskDetails.push(taskDetail);

        } else if (isInProgress) {
          // Track in-progress tasks
          empPerf.inProgressTasks++;

          let endDate;
          try {
            endDate = new Date(task.endDateTime);
            if (isNaN(endDate.getTime())) {
              endDate = new Date();
            }
          } catch (error) {
            endDate = new Date();
          }

          const taskDetail = {
            title: task.title || 'Untitled Task',
            endDate: endDate,
            completionDate: null,
            isOnTime: null
          };

          empPerf.taskDetails.push(taskDetail);
        } else if (isPending) {
          // Track only pending tasks (NOT reopened or other statuses)
          empPerf.pendingTasks++;

          let endDate;
          try {
            endDate = new Date(task.endDateTime);
            if (isNaN(endDate.getTime())) {
              endDate = new Date();
            }
          } catch (error) {
            endDate = new Date();
          }

          const taskDetail = {
            title: task.title || 'Untitled Task',
            endDate: endDate,
            completionDate: null,
            isOnTime: null
          };

          empPerf.taskDetails.push(taskDetail);
        } else if (isReopened) {
          // Reopened tasks - tracked but not counted in main status categories
          let endDate;
          try {
            endDate = new Date(task.endDateTime);
            if (isNaN(endDate.getTime())) {
              endDate = new Date();
            }
          } catch (error) {
            endDate = new Date();
          }

          const taskDetail = {
            title: task.title || 'Untitled Task',
            endDate: endDate,
            completionDate: null,
            isOnTime: null
          };

          empPerf.taskDetails.push(taskDetail);
        }
      });
    });

    performance.forEach((emp) => {
      emp.completionRate = emp.totalAssignedTasks > 0
        ? (emp.completedTasks / emp.totalAssignedTasks) * 100
        : 0;
      emp.onTimeRate = emp.completedTasks > 0
        ? (emp.onTimeTasks / emp.completedTasks) * 100
        : 0;
      emp.performanceScore = emp.completionRate;

      console.log(`👤 Final metrics for ${emp.name}:`, {
        completedTasks: emp.completedTasks,
        inProgressTasks: emp.inProgressTasks,
        pendingTasks: emp.pendingTasks,
        totalAssigned: emp.totalAssignedTasks,
        completionRate: emp.completionRate.toFixed(1) + '%',
        onTimeRate: emp.onTimeRate.toFixed(1) + '%',
        performanceScore: emp.performanceScore.toFixed(1)
      });
    });

    const result = Array.from(performance.values());

    console.log('📋 Performance calculation results:');
    console.table(result.map(emp => ({
      name: emp.name,
      completedTasks: emp.completedTasks,
      inProgressTasks: emp.inProgressTasks,
      pendingTasks: emp.pendingTasks,
      totalAssigned: emp.totalAssignedTasks,
      completionRate: emp.completionRate.toFixed(1) + '%',
      onTimeRate: emp.onTimeRate.toFixed(1) + '%',
      performanceScore: emp.performanceScore.toFixed(1)
    })));

    console.groupEnd();
    return result;
  }, [employees, tasks, debugMode]);

  // Analytics functions
  const calculatePerformanceTrends = useCallback((performanceData) => {
    return performanceData.map(emp => {
      const tasksByMonth = emp.taskDetails.reduce((acc, task) => {
        if (task.completionDate) {
          const month = new Date(task.completionDate).getMonth();
          acc[month] = (acc[month] || 0) + 1;
        }
        return acc;
      }, {});

      const months = Object.keys(tasksByMonth).sort();
      const trend = months.length > 1
        ? (tasksByMonth[months[months.length - 1]] - tasksByMonth[months[0]]) / months.length
        : 0;

      return { ...emp, trend, monthlyPerformance: tasksByMonth };
    });
  }, []);

  const generatePerformanceAlerts = useCallback((performanceData) => {
    const alerts = [];
    performanceData.forEach(emp => {
      if (emp.performanceScore < alertThresholds.lowPerformance) {
        alerts.push({
          id: `low-perf-${emp.id}`,
          type: 'warning',
          employee: emp.name,
          message: `${emp.name} has low performance (${emp.performanceScore.toFixed(1)}%)`,
          severity: 'high'
        });
      }

      if (emp.onTimeRate < 70 && emp.completedTasks > 0) {
        alerts.push({
          id: `late-tasks-${emp.id}`,
          type: 'warning',
          employee: emp.name,
          message: `${emp.name} has high late task rate (${emp.onTimeRate.toFixed(1)}%)`,
          severity: 'medium'
        });
      }

      if (emp.completionRate < alertThresholds.lowCompletionRate && emp.totalAssignedTasks > 0) {
        alerts.push({
          id: `low-completion-${emp.id}`,
          type: 'error',
          employee: emp.name,
          message: `${emp.name} has low completion rate (${emp.completionRate.toFixed(1)}%)`,
          severity: 'high'
        });
      }
    });

    return alerts;
  }, [alertThresholds]);

  // Export functionality
  const exportData = useCallback((type, data) => {
    const timestamp = new Date().toISOString().split('T')[0];

    if (type === 'csv') {
      const csvContent = [
        ['Employee', 'Completed Tasks', 'Assigned Tasks', 'Completion Rate', 'On-Time Rate', 'Performance Score'].join(','),
        ...data.map(emp => [
          emp.name,
          emp.completedTasks,
          emp.totalAssignedTasks,
          emp.completionRate.toFixed(1) + '%',
          emp.onTimeRate.toFixed(1) + '%',
          emp.performanceScore.toFixed(1)
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `employee-performance-${timestamp}.csv`;
      link.click();
    } else if (type === 'json') {
      const jsonData = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `employee-performance-${timestamp}.json`;
      link.click();
    }
  }, []);

  // Individual Employee Performance Functions
  const viewIndividualPerformance = useCallback((employee) => {
    setSelectedEmployee(employee);
    setEmployeePerformanceView(true);

    const empId = employee.id;
    const empName = employee.name;

    console.log('🔍 Filtering tasks for employee:', { empId, empName, totalTasks: tasks.length });

    const employeeTasks = tasks.filter(task => {
      if (!task.assignedTo) return false;

      let assignees = [];
      if (Array.isArray(task.assignedTo)) {
        assignees = task.assignedTo;
      } else if (typeof task.assignedTo === 'string') {
        assignees = [task.assignedTo];
      } else if (typeof task.assignedTo === 'object' && task.assignedTo._id) {
        assignees = [task.assignedTo._id];
      } else if (typeof task.assignedTo === 'object' && task.assignedTo.id) {
        assignees = [task.assignedTo.id];
      } else if (typeof task.assignedTo === 'object' && task.assignedTo.name) {
        assignees = [task.assignedTo.name];
      } else {
        assignees = [task.assignedTo];
      }

      const isAssigned = assignees.some(assignee => {
        const assigneeId = typeof assignee === 'string' ? assignee : (assignee._id || assignee.id || assignee.name || String(assignee));
        const assigneeName = typeof assignee === 'object' ? (assignee.name || assignee.teamMemberName) : null;

        return assigneeId === empId ||
          assigneeId === empName ||
          assigneeName === empName ||
          (assigneeId && assigneeId.toLowerCase() === empName.toLowerCase()) ||
          (assigneeId && empId && assigneeId.includes(empId)) ||
          (assigneeId && empId && empId.includes(assigneeId));
      });

      return isAssigned;
    });

    console.log('📋 Found tasks for employee:', { empName, taskCount: employeeTasks.length });
    setEmployeeTasks(employeeTasks);
  }, [tasks]);

  // Individual Employee Chart Component
  const IndividualEmployeeChart = ({ performanceData }) => {
    if (!performanceData.monthlyPerformance || Object.keys(performanceData.monthlyPerformance).length === 0) {
      return (
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Chart Data Available</h3>
          <p className="text-gray-600">Complete some tasks to see performance charts here.</p>
        </div>
      );
    }

    const monthlyData = performanceData.monthlyPerformance;
    const months = Object.keys(monthlyData).sort();

    const chartData = {
      labels: months,
      datasets: [{
        label: getMetricLabel(employeeChartMetric),
        data: months.map(month => {
          switch (employeeChartMetric) {
            case 'completedTasks': return monthlyData[month].completed;
            case 'taskCount': return monthlyData[month].completed;
            default: return monthlyData[month].completed;
          }
        }),
        backgroundColor: employeeChartType === 'line'
          ? 'rgba(59, 130, 246, 0.1)'
          : [
            'rgba(59, 130, 246, 0.8)',
            'rgba(16, 185, 129, 0.8)',
            'rgba(245, 158, 11, 0.8)',
            'rgba(239, 68, 68, 0.8)',
            'rgba(139, 92, 246, 0.8)',
            'rgba(236, 72, 153, 0.8)'
          ],
        borderColor: employeeChartType === 'line'
          ? 'rgb(59, 130, 246)'
          : [
            'rgb(59, 130, 246)',
            'rgb(16, 185, 129)',
            'rgb(245, 158, 11)',
            'rgb(239, 68, 68)',
            'rgb(139, 92, 246)',
            'rgb(236, 72, 153)'
          ],
        borderWidth: employeeChartType === 'line' ? 3 : 2,
        fill: employeeChartType === 'line',
        tension: employeeChartType === 'line' ? 0.4 : 0,
        pointBackgroundColor: employeeChartType === 'line' ? 'rgb(59, 130, 246)' : undefined,
        pointBorderColor: employeeChartType === 'line' ? '#ffffff' : undefined,
        pointBorderWidth: employeeChartType === 'line' ? 2 : undefined,
        pointRadius: employeeChartType === 'line' ? 6 : undefined,
        pointHoverRadius: employeeChartType === 'line' ? 8 : undefined,
        borderRadius: employeeChartType === 'bar' ? 8 : undefined,
        borderSkipped: employeeChartType === 'bar' ? false : undefined
      }]
    };

    const pieChartData = {
      labels: ['Completed On-Time', 'Completed Late', 'Pending'],
      datasets: [{
        data: [
          performanceData.onTimeTasks,
          performanceData.lateTasks,
          performanceData.totalAssignedTasks - performanceData.completedTasks
        ],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',
          'rgba(251, 191, 36, 0.8)',
          'rgba(156, 163, 175, 0.8)'
        ],
        borderColor: [
          'rgb(34, 197, 94)',
          'rgb(251, 191, 36)',
          'rgb(156, 163, 175)'
        ],
        borderWidth: 3,
        cutout: employeeChartType === 'doughnut' ? '60%' : '0%',
        hoverOffset: 4
      }]
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: employeeChartType === 'pie' ? 'right' : 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
              family: 'Inter, system-ui, sans-serif'
            }
          }
        },
        title: {
          display: true,
          text: `${performanceData.name}'s ${getMetricLabel(employeeChartMetric)} Performance`,
          font: {
            size: 16,
            weight: 'bold',
            family: 'Inter, system-ui, sans-serif'
          },
          color: '#1f2937',
          padding: 20
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.95)',
          titleColor: '#f9fafb',
          bodyColor: '#f9fafb',
          borderColor: '#374151',
          borderWidth: 1,
          cornerRadius: 8,
          padding: 12,
          titleFont: {
            size: 14,
            weight: 'bold'
          },
          bodyFont: {
            size: 12
          }
        }
      },
      scales: employeeChartType !== 'pie' ? {
        x: {
          grid: {
            display: true,
            color: 'rgba(243, 244, 246, 0.8)'
          },
          ticks: {
            font: {
              size: 11,
              family: 'Inter, system-ui, sans-serif'
            },
            color: '#6b7280'
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            display: true,
            color: 'rgba(243, 244, 246, 0.8)'
          },
          ticks: {
            font: {
              size: 11,
              family: 'Inter, system-ui, sans-serif'
            },
            color: '#6b7280'
          }
        }
      } : undefined
    };

    const renderChart = () => {
      if (employeeChartType === 'bar') {
        return <Bar data={chartData} options={chartOptions} />;
      } else if (employeeChartType === 'line') {
        return <Line data={chartData} options={chartOptions} />;
      } else if (employeeChartType === 'pie') {
        return <Doughnut data={pieChartData} options={chartOptions} />;
      }
      return null;
    };

    return (
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 space-y-4 lg:space-y-0">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Performance Analytics</h3>
            <p className="text-gray-600 mt-1">Interactive charts showing detailed performance metrics</p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <select
              value={employeeChartType}
              onChange={(e) => setEmployeeChartType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm font-medium"
            >
              <option value="bar" className="text-black">📊 Bar Chart</option>
              <option value="line" className="text-black">📈 Line Chart</option>
              <option value="pie" className="text-black">🥧 Pie Chart</option>
            </select>
            <select
              value={employeeChartMetric}
              onChange={(e) => setEmployeeChartMetric(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm font-medium"
            >
              <option value="completedTasks" className="text-black">✅ Completed Tasks</option>
              <option value="taskCount" className="text-black">📋 Task Count</option>
            </select>
          </div>
        </div>

        <div className="h-96 flex items-center justify-center">
          {renderChart()}
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-center text-sm">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-600">
              {Object.keys(performanceData.monthlyPerformance).length}
            </div>
            <div className="text-gray-600">Active Months</div>
          </div>
          <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">
              {performanceData.completedTasks}
            </div>
            <div className="text-gray-600">Total Completed Tasks</div>
          </div>
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-600">
              {(performanceData.completedTasks / Math.max(Object.keys(performanceData.monthlyPerformance).length, 1)).toFixed(1)}
            </div>
            <div className="text-gray-600">Monthly Average</div>
          </div>
        </div>
      </div>
    );
  };

  // Download report functions
  const downloadEmployeeReport = useCallback((employee, tasks, performanceData) => {
    const timestamp = new Date().toISOString().split('T')[0];

    const doc = new jsPDF();

    doc.setFontSize(20);
    doc.setTextColor(37, 99, 235);
    doc.text('Employee Performance Report', 14, 22);

    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Employee: ${employee.name}`, 14, 35);
    doc.text(`Report Date: ${formatDate(new Date())}`, 14, 42);
    doc.text(`Report Period: All Time`, 14, 49);

    doc.setFontSize(14);
    doc.setTextColor(37, 99, 235);
    doc.text('Performance Summary', 14, 62);

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    const summaryData = [
      ['Completed Tasks', performanceData.completedTasks],
      ['Total Assigned Tasks', performanceData.totalAssignedTasks],
      ['Completion Rate', `${performanceData.completionRate.toFixed(1)}%`],
      ['On-Time Rate', `${performanceData.onTimeRate.toFixed(1)}%`],
      ['Performance Score', `${performanceData.performanceScore.toFixed(1)}%`],
      ['On-Time Tasks', performanceData.onTimeTasks],
      ['Late Tasks', performanceData.lateTasks],
    ];

    autoTable(doc, {
      startY: 68,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    const finalY = doc.lastAutoTable.finalY || 68;
    doc.setFontSize(14);
    doc.setTextColor(37, 99, 235);
    doc.text('Task Details', 14, finalY + 15);

    const taskData = performanceData.taskDetails.map(task => [
      task.title || 'Untitled Task',
      task.status || 'Pending',
      task.priority || 'Medium',
      formatDate(task.endDate),
      formatDate(task.completionDate),
      task.isOnTime === null ? 'Pending' : (task.isOnTime ? 'On Time' : 'Late')
    ]);

    autoTable(doc, {
      startY: finalY + 20,
      head: [['Task', 'Status', 'Priority', 'Due Date', 'Completed', 'Performance']],
      body: taskData,
      theme: 'grid',
      headStyles: { fillColor: [37, 99, 235] },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 20 },
        2: { cellWidth: 20 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 },
        5: { cellWidth: 20 }
      }
    });

    doc.save(`${employee.name.replace(/\s+/g, '_')}_Performance_Report_${timestamp}.pdf`);
  }, [formatDate]);

  const downloadEmployeeReportCSV = useCallback((employee, tasks, performanceData) => {
    const timestamp = new Date().toISOString().split('T')[0];

    const csvRows = [];

    csvRows.push('Employee Performance Report');
    csvRows.push(`Employee,${employee.name}`);
    csvRows.push(`Report Date,${formatDate(new Date())}`);
    csvRows.push('');

    csvRows.push('Performance Summary');
    csvRows.push('Metric,Value');
    csvRows.push(`Completed Tasks,${performanceData.completedTasks}`);
    csvRows.push(`Total Assigned Tasks,${performanceData.totalAssignedTasks}`);
    csvRows.push(`Completion Rate,${performanceData.completionRate.toFixed(1)}%`);
    csvRows.push(`On-Time Rate,${performanceData.onTimeRate.toFixed(1)}%`);
    csvRows.push(`Performance Score,${performanceData.performanceScore.toFixed(1)}%`);
    csvRows.push(`On-Time Tasks,${performanceData.onTimeTasks}`);
    csvRows.push(`Late Tasks,${performanceData.lateTasks}`);
    csvRows.push('');

    csvRows.push('Task Details');
    csvRows.push('Task,Status,Priority,Due Date,Completed,Performance');

    performanceData.taskDetails.forEach(task => {
      csvRows.push([
        `"${task.title || 'Untitled Task'}"`,
        task.status || 'Pending',
        task.priority || 'Medium',
        formatDate(task.endDate),
        formatDate(task.completionDate),
        task.isOnTime === null ? 'Pending' : (task.isOnTime ? 'On Time' : 'Late')
      ].join(','));
    });

    const csvContent = csvRows.join('\n');
    // Add UTF-8 BOM for better Excel compatibility
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${employee.name.replace(/\s+/g, '_')}_Performance_Report_${timestamp}.csv`;
    link.click();
  }, [formatDate]);

  // Individual Employee Performance View
  const IndividualEmployeePerformance = () => {
    if (!selectedEmployee) return null;

    const individualPerformance = calculateIndividualPerformance(selectedEmployee, employeeTasks);

    if (!individualPerformance) {
      return (
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="w-full px-0" style={{ maxWidth: '100%', margin: 0, padding: 0 }}>
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Found</h3>
              <p className="text-gray-600">This employee doesn't have any performance data yet.</p>
            </div>
          </div>
        </div>
      );
    }

    // Main return for when individualPerformance exists
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full px-0" style={{ maxWidth: '100%', margin: 0, padding: 0 }}>
          {/* Header */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
              {/* Header Row: Name + Buttons */}
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center w-full mb-6">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setEmployeePerformanceView(false)}
                    className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                  </button>
                  <h1 className="text-3xl font-bold">{individualPerformance.name}</h1>
                </div>
                <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                  <button
                    onClick={() => downloadEmployeeReport(selectedEmployee, employeeTasks, individualPerformance)}
                    className="px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-all duration-300 font-semibold shadow-lg flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Download PDF Report</span>
                  </button>
                  <button
                    onClick={() => downloadEmployeeReportCSV(selectedEmployee, employeeTasks, individualPerformance)}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-300 font-semibold shadow-lg flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Download CSV Report</span>
                  </button>
                </div>
              </div>
              {/* Performance Summary Cards Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <div className="text-2xl font-bold">{individualPerformance.completedTasks}</div>
                  <div className="text-blue-100 text-sm">Completed Tasks</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <div className="text-2xl font-bold">{individualPerformance.completedTasks}/{individualPerformance.totalAssignedTasks}</div>
                  <div className="text-blue-100 text-sm">Tasks Completed</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <div className="text-2xl font-bold">{individualPerformance.completionRate.toFixed(1)}%</div>
                  <div classNamve="text-blue-100 text-sm">Completion Rate</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <div className="text-2xl font-bold">{individualPerformance.onTimeRate.toFixed(1)}%</div>
                  <div className="text-blue-100 text-sm">On-Time Rate</div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Performance Charts */}
            <div className="lg:col-span-2">
              <IndividualEmployeeChart performanceData={individualPerformance} />
            </div>

            {/* Performance Metrics */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-lg font-semibold mb-4">Performance Metrics</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">On-Time Rate</span>
                      <span className="text-sm font-medium">{individualPerformance.onTimeRate.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: `${Math.min(individualPerformance.onTimeRate, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">Completion Rate</span>
                      <span className="text-sm font-medium">{individualPerformance.completionRate.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${Math.min(individualPerformance.completionRate, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-xl font-bold text-green-600">{individualPerformance.onTimeTasks}</div>
                      <div className="text-xs text-gray-600">On-Time Tasks</div>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-lg">
                      <div className="text-xl font-bold text-yellow-600">{individualPerformance.lateTasks}</div>
                      <div className="text-xs text-gray-600">Late Tasks</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Task Distribution */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-lg font-semibold mb-4">Task Distribution</h3>
                <div className="space-y-3">
                  {Object.entries(individualPerformance.monthlyPerformance).map(([month, data]) => (
                    <div key={month} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{month}</span>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm font-medium">{data.completed || 0} tasks</span>
                      </div>
                    </div>
                  ))}
                  {Object.keys(individualPerformance.monthlyPerformance).length === 0 && (
                    <div className="text-center text-gray-500 text-sm py-4">
                      No monthly data available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Task Details Table */}
          <div className="mt-8 bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold">Task Details</h3>
              <p className="text-gray-600">Complete breakdown of all assigned tasks</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Task (Click to update)
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Performance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {individualPerformance.taskDetails.map((task, index) => (
                    <tr
                      key={index}
                      onClick={() => handleTaskClick(employeeTasks[index])}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{task.title || 'Untitled Task'}</div>
                        <div className="text-sm text-gray-500">Priority: {task.priority || 'Medium'}</div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${task.completionDate
                          ? 'bg-green-100 text-green-800'
                          : task.status && (task.status.toLowerCase().includes('progress') ||
                            task.status.toLowerCase().includes('inprogress') ||
                            task.status.toLowerCase().includes('ongoing') ||
                            task.status.toLowerCase().includes('active') ||
                            task.status.toLowerCase().includes('working'))
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-yellow-100 text-yellow-800'
                          }`}>
                          {task.completionDate
                            ? 'Completed'
                            : task.status && (task.status.toLowerCase().includes('progress') ||
                              task.status.toLowerCase().includes('inprogress') ||
                              task.status.toLowerCase().includes('ongoing') ||
                              task.status.toLowerCase().includes('active') ||
                              task.status.toLowerCase().includes('working'))
                              ? 'In Progress'
                              : 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center text-sm text-gray-900">
                        {formatDate(task.endDate)}
                      </td>
                      <td className="px-6 py-4 text-center text-sm text-gray-900">
                        {formatDate(task.completionDate)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${task.isOnTime === null
                          ? 'bg-blue-100 text-blue-800'
                          : task.isOnTime
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                          }`}>
                          {task.isOnTime === null ? 'Pending' : task.isOnTime ? 'On Time' : 'Late'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {individualPerformance.taskDetails.length === 0 && (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks Found</h3>
                  <p className="text-gray-600">This employee doesn't have any assigned tasks yet.</p>
                </div>
              )}
            </div>
          </div>

          {/* Update Status Modal */}
          {showUpdateStatusModal && selectedTaskForUpdate && (
            <UpdateStatus
              task={selectedTaskForUpdate}
              onClose={() => {
                setShowUpdateStatusModal(false);
                setSelectedTaskForUpdate(null);
              }}
              onStatusUpdate={(updatedTask) => {
                fetchTasksWithCreditPoints();
                fetchRegularTasks();
                setShowUpdateStatusModal(false);
                setSelectedTaskForUpdate(null);
              }}
            />
          )}
        </div>
      </div>
    );
  };

  // Filter and sort data
  const filteredAndSortedData = React.useMemo(() => {
    let filtered = [...performanceData];

    // Filter only employees with assigned tasks
    filtered = filtered.filter(emp => emp.totalAssignedTasks > 0);

    // Search term filter
    if (searchTerm) {
      filtered = filtered.filter(emp =>
        emp.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Department filter
    if (selectedDepartment !== 'all') {
      filtered = filtered.filter(emp => {
        const employee = employees.find(e =>
          (e._id || e.id || e.employeeId) === emp.id
        );

        if (!employee) return false;

        if (Array.isArray(employee.department)) {
          return employee.department.includes(selectedDepartment) ||
            employee.department.some(deptId => String(deptId) === String(selectedDepartment));
        }

        return employee.department === selectedDepartment ||
          employee.departmentId === selectedDepartment ||
          employee.department?._id === selectedDepartment ||
          employee.department?.id === selectedDepartment;
      });
    }

    // Department employee filter
    if (selectedDepartmentEmployee !== 'all') {
      filtered = filtered.filter(emp => emp.id === selectedDepartmentEmployee);
    }

    // Performance category filter
    switch (filterBy) {
      case 'topPerformers':
        filtered = filtered.filter(emp => emp.performanceScore >= 70);
        break;
      case 'needsImprovement':
        filtered = filtered.filter(emp => emp.performanceScore < 50 && emp.completedTasks > 0);
        break;
      case 'noActivity':
        filtered = filtered.filter(emp => emp.completedTasks === 0);
        break;
      default:
        break;
    }

    // Date range filter
    if (dateRange.start && dateRange.end) {
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);

      filtered = filtered.map(emp => ({
        ...emp,
        taskDetails: emp.taskDetails.filter(task => {
          const taskDate = new Date(task.completionDate);
          return taskDate >= startDate && taskDate <= endDate;
        })
      })).filter(emp => emp.taskDetails.length > 0);
    }

    // Sort 
    return filtered.sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [performanceData, sortBy, sortOrder, searchTerm, filterBy, dateRange,
    selectedDepartment, selectedDepartmentEmployee, selectedStatus, selectedPriority,
    employees, tasks]);

  // Enhanced data with analytics
  const enhancedPerformanceData = React.useMemo(() => {
    return calculatePerformanceTrends(filteredAndSortedData);
  }, [filteredAndSortedData, calculatePerformanceTrends]);

  // Generate alerts
  useEffect(() => {
    if (enhancedPerformanceData.length > 0) {
      const alerts = generatePerformanceAlerts(enhancedPerformanceData);
      setNotifications(alerts);
    }
  }, [enhancedPerformanceData, generatePerformanceAlerts]);

  // Chart data preparation
  const chartData = React.useMemo(() => {
    if (!enhancedPerformanceData.length) return null;

    const topEmployees = enhancedPerformanceData.slice(0, 10);
    const labels = topEmployees.map(emp => emp.name);

    const getDataValues = (employees, metric) => {
      return employees.map(emp => {
        switch (metric) {
          case 'completedTasks': return emp.completedTasks;
          case 'completionRate': return emp.completionRate;
          case 'onTimeRate': return emp.onTimeRate;
          case 'performanceScore': return emp.performanceScore;
          default: return emp.completedTasks;
        }
      });
    };

    const dataValues = getDataValues(topEmployees, chartMetric);

    const gradientColors = [
      'rgba(59, 130, 246, 0.8)',
      'rgba(16, 185, 129, 0.8)',
      'rgba(245, 158, 11, 0.8)',
      'rgba(239, 68, 68, 0.8)',
      'rgba(139, 92, 246, 0.8)',
      'rgba(236, 72, 153, 0.8)',
      'rgba(6, 182, 212, 0.8)',
      'rgba(34, 197, 94, 0.8)',
      'rgba(168, 85, 247, 0.8)',
      'rgba(251, 146, 60, 0.8)'
    ];

    const borderColors = gradientColors.map(color => color.replace('0.8', '1'));

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
              family: 'Inter, system-ui, sans-serif'
            }
          }
        },
        title: {
          display: true,
          text: `Employee Performance - ${getMetricLabel(chartMetric)}`,
          font: {
            size: 18,
            weight: 'bold',
            family: 'Inter, system-ui, sans-serif'
          },
          color: '#1f2937',
          padding: 20
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.95)',
          titleColor: '#f9fafb',
          bodyColor: '#f9fafb',
          borderColor: '#374151',
          borderWidth: 1,
          cornerRadius: 8,
          padding: 12,
          titleFont: {
            size: 14,
            weight: 'bold'
          },
          bodyFont: {
            size: 12
          }
        }
      },
      scales: chartType !== 'doughnut' ? {
        x: {
          grid: {
            display: true,
            color: 'rgba(243, 244, 246, 0.8)'
          },
          ticks: {
            font: {
              size: 11,
              family: 'Inter, system-ui, sans-serif'
            },
            color: '#6b7280'
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            display: true,
            color: 'rgba(243, 244, 246, 0.8)'
          },
          ticks: {
            font: {
              size: 11,
              family: 'Inter, system-ui, sans-serif'
            },
            color: '#6b7280'
          }
        }
      } : undefined
    };

    if (chartType === 'line') {
      const lineData = {
        labels,
        datasets: [{
          label: getMetricLabel(chartMetric),
          data: dataValues,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: 'rgb(59, 130, 246)',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointHoverBackgroundColor: 'rgb(37, 99, 235)',
          pointHoverBorderColor: '#ffffff',
          pointHoverBorderWidth: 3
        }]
      };

      return (
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 space-y-4 lg:space-y-0">
            <h3 className="text-xl font-semibold text-gray-900">Performance Trends</h3>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
              >
                <option value="line">Line Chart</option>
                <option value="bar">Bar Chart</option>
                <option value="doughnut">Doughnut Chart</option>
              </select>
              <select
                value={chartMetric}
                onChange={(e) => setChartMetric(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
              >
                <option value="completedTasks">Completed Tasks</option>
                <option value="completionRate">Completion Rate</option>
                <option value="onTimeRate">On-Time Rate</option>
                <option value="performanceScore">Performance Score</option>
              </select>
            </div>
          </div>
          <div className="h-96">
            <Line data={lineData} options={chartOptions} />
          </div>
        </div>
      );
    }

    if (chartType === 'bar') {
      const barData = {
        labels,
        datasets: [{
          label: getMetricLabel(chartMetric),
          data: dataValues,
          backgroundColor: gradientColors,
          borderColor: borderColors,
          borderWidth: 2,
          borderRadius: 8,
          borderSkipped: false,
        }]
      };

      return (
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 space-y-4 lg:space-y-0">
            <h3 className="text-xl font-semibold text-gray-900">Top Performers</h3>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
              >
                <option value="line">Line Chart</option>
                <option value="bar">Bar Chart</option>
                <option value="doughnut">Doughnut Chart</option>
              </select>
              <select
                value={chartMetric}
                onChange={(e) => setChartMetric(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
              >
                <option value="completedTasks">Completed Tasks</option>
                <option value="completionRate">Completion Rate</option>
                <option value="onTimeRate">On-Time Rate</option>
                <option value="performanceScore">Performance Score</option>
              </select>
            </div>
          </div>
          <div className="h-96">
            <Bar data={barData} options={chartOptions} />
          </div>
        </div>
      );
    }

    if (chartType === 'doughnut') {
      const excellent = enhancedPerformanceData.filter(emp => emp.performanceScore >= 80).length;
      const good = enhancedPerformanceData.filter(emp => emp.performanceScore >= 60 && emp.performanceScore < 80).length;
      const average = enhancedPerformanceData.filter(emp => emp.performanceScore >= 40 && emp.performanceScore < 60).length;
      const needsImprovement = enhancedPerformanceData.filter(emp => emp.performanceScore < 40).length;

      const doughnutData = {
        labels: ['Excellent (80+)', 'Good (60-79)', 'Average (40-59)', 'Needs Improvement (<40)'],
        datasets: [{
          data: [excellent, good, average, needsImprovement],
          backgroundColor: [
            'rgba(34, 197, 94, 0.8)',
            'rgba(59, 130, 246, 0.8)',
            'rgba(245, 158, 11, 0.8)',
            'rgba(239, 68, 68, 0.8)'
          ],
          borderColor: [
            'rgba(34, 197, 94, 1)',
            'rgba(59, 130, 246, 1)',
            'rgba(245, 158, 11, 1)',
            'rgba(239, 68, 68, 1)'
          ],
          borderWidth: 3,
          cutout: '60%',
          hoverOffset: 4
        }]
      };

      const doughnutOptions = {
        ...chartOptions,
        plugins: {
          ...chartOptions.plugins,
          legend: {
            position: 'right',
            labels: {
              usePointStyle: true,
              padding: 20,
              font: {
                size: 12,
                family: 'Inter, system-ui, sans-serif'
              }
            }
          }
        }
      };

      return (
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 space-y-4 lg:space-y-0">
            <h3 className="text-xl font-semibold text-gray-900">Performance Distribution</h3>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
              >
                <option value="line">Line Chart</option>
                <option value="bar">Bar Chart</option>
                <option value="doughnut">Doughnut Chart</option>
              </select>
              <select
                value={chartMetric}
                onChange={(e) => setChartMetric(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
              >
                <option value="completedTasks">Completed Tasks</option>
                <option value="completionRate">Completion Rate</option>
                <option value="onTimeRate">On-Time Rate</option>
                <option value="performanceScore">Performance Score</option>
              </select>
            </div>
          </div>
          <div className="h-96 flex items-center justify-center">
            <div className="w-full max-w-md relative">
              <Doughnut data={doughnutData} options={doughnutOptions} />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{enhancedPerformanceData.length}</div>
                  <div className="text-sm text-gray-500">Total Employees</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  }, [enhancedPerformanceData, chartMetric, chartType, getMetricLabel]);

  // Performance Card component
  const PerformanceCard = ({ employee, index }) => (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
            <span className="text-white font-semibold text-lg">
              {employee.name ? employee.name.charAt(0).toUpperCase() : 'U'}
            </span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{employee.name}</h3>
            <p className="text-sm text-gray-500">Rank #{index + 1}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-600">{employee.completedTasks}</div>
          <div className="text-xs text-gray-500">completed</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <div className="text-sm font-medium text-gray-900">{employee.completedTasks}/{employee.totalAssignedTasks}</div>
          <div className="text-xs text-gray-500">Tasks</div>
        </div>
        <div className="text-center">
          <div className="text-sm font-medium text-gray-900">{employee.completionRate.toFixed(1)}%</div>
          <div className="text-xs text-gray-500">Completion</div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">On-time Rate</span>
          <span className="text-sm font-medium">{employee.onTimeRate.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-green-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(employee.onTimeRate, 100)}%` }}
          ></div>
        </div>
      </div>

      <div className="mt-4 text-center">
        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getPerformanceBadge(employee.performanceScore)}`}>
          Performance: {employee.performanceScore.toFixed(1)}%
        </span>
      </div>

      <button
        onClick={() => viewIndividualPerformance(employee)}
        className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
      >
        View Detailed Performance
      </button>
    </div>
  );

  // Analytics Sidebar component
  const AnalyticsSidebar = () => (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
      <h3 className="text-lg font-semibold mb-4">Performance Analytics</h3>

      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Performance Distribution</h4>
        <div className="space-y-2">
          {[
            { label: 'Excellent (80+)', count: enhancedPerformanceData.filter(e => e.performanceScore >= 80).length, color: 'bg-green-500' },
            { label: 'Good (60-79)', count: enhancedPerformanceData.filter(e => e.performanceScore >= 60 && e.performanceScore < 80).length, color: 'bg-blue-500' },
            { label: 'Average (40-59)', count: enhancedPerformanceData.filter(e => e.performanceScore >= 40 && e.performanceScore < 60).length, color: 'bg-yellow-500' },
            { label: 'Needs Improvement (<40)', count: enhancedPerformanceData.filter(e => e.performanceScore < 40).length, color: 'bg-red-500' }
          ].map((item, idx) => (
            <div key={idx} className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                <span className="text-sm text-gray-600">{item.label}</span>
              </div>
              <span className="text-sm font-medium">{item.count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Team Metrics</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="text-xl font-bold text-blue-600">
              {enhancedPerformanceData.reduce((sum, emp) => sum + emp.completedTasks, 0)}
            </div>
            <div className="text-xs text-gray-600">Total Completed</div>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-xl font-bold text-green-600">
              {enhancedPerformanceData.length > 0 ?
                (enhancedPerformanceData.reduce((sum, emp) => sum + emp.completionRate, 0) / enhancedPerformanceData.length).toFixed(1) :
                '0'}%
            </div>
            <div className="text-xs text-gray-600">Avg Completion</div>
          </div>
        </div>
      </div>

      {notifications.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Performance Alerts</h4>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {notifications.slice(0, 3).map((alert, idx) => (
              <div key={idx} className={`p-2 rounded text-xs ${alert.severity === 'high' ? 'bg-red-100 text-red-700 border border-red-200' :
                'bg-yellow-100 text-yellow-700 border border-yellow-200'
                }`}>
                <div className="font-medium">{alert.employee}</div>
                <div>{alert.message}</div>
              </div>
            ))}
            {notifications.length > 3 && (
              <div className="text-xs text-gray-500 text-center">
                +{notifications.length - 3} more alerts
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Employee table row component
  const EmployeeRow = React.memo(({ employee, index, getPerformanceBadge }) => (
    <tr key={employee.id} className="hover:bg-gray-50 transition-colors">
      <td className="px-6 py-4">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {employee.name ? employee.name.charAt(0).toUpperCase() : 'U'}
              </span>
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-900">{employee.name}</div>
            <div className="text-sm text-gray-500">Rank #{index + 1}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 text-center">
        <div className="text-xl font-bold text-gray-900">
          {employee.completedTasks}
        </div>
        <div className="text-xs text-gray-500">completed</div>
      </td>
      <td className="px-6 py-4 text-center">
        <div className="text-sm text-gray-900">
          <span className="font-semibold">{employee.completedTasks}</span> / {employee.totalAssignedTasks}
        </div>
        <div className="text-xs text-gray-500">
          {employee.onTimeTasks} on-time, {employee.lateTasks} late
        </div>
      </td>
      <td className="px-6 py-4 text-center">
        <div className="flex items-center justify-center">
          <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(employee.completionRate, 100)}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium text-gray-900">
            {employee.completionRate.toFixed(1)}%
          </span>
        </div>
      </td>
      <td className="px-6 py-4 text-center">
        <div className="flex items-center justify-center">
          <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(employee.onTimeRate, 100)}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium text-gray-900">
            {employee.onTimeRate.toFixed(1)}%
          </span>
        </div>
      </td>
      <td className="px-6 py-4 text-center">
        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getPerformanceBadge(employee.performanceScore)}`}>
          {employee.performanceScore.toFixed(1)}%
        </span>
      </td>
      <td className="px-6 py-4 text-center">
        <button
          onClick={() => viewIndividualPerformance(employee)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          View Details
        </button>
      </td>
    </tr>
  ));

  // Performance badge function
  const getPerformanceBadge = useCallback((score) => {
    if (score >= 80) return 'bg-green-100 text-green-800 border-green-200';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    if (score >= 40) return 'bg-orange-100 text-orange-800 border-orange-200';
    return 'bg-red-100 text-red-800 border-red-200';
  }, []);

  // Handle functions
  const handleSort = useCallback((column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  }, [sortBy, sortOrder]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🚀 Starting optimized data load with credit points APIs...');

      const userInfo = getUserInfo();
      setCurrentUserRole(userInfo?.role);

      try {
        const [tasksData, employeesData, departmentsData] = await Promise.all([
          fetchTasksWithCreditPoints(),
          fetchEmployeesWithCreditPoints(),
          fetchDepartments()
        ]);

        console.log('📊 Credit points data loaded successfully:', {
          tasks: tasksData.length,
          employees: employeesData.length,
          departments: departmentsData.length
        });

        setTasks(tasksData);
        setDepartments(departmentsData);

        if (employeesData.length > 0 && (employeesData[0].name || employeesData[0].teamMemberName || employeesData[0].firstName)) {
          setEmployees(employeesData);
        } else {
          console.log('📋 Employee details not found in credit points API, fetching from employees API...');
          const regularEmployees = await fetchEmployees();
          setEmployees(regularEmployees);
        }

      } catch (creditPointsError) {
        console.warn('⚠️ Credit points APIs failed, falling back to regular APIs:', creditPointsError.message);

        const [employeeData, taskData, departmentsData] = await Promise.all([
          fetchEmployees(),
          fetchRegularTasks(),
          fetchDepartments()
        ]);

        setEmployees(employeeData);
        setTasks(taskData);
        setDepartments(departmentsData);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading performance data:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [fetchTasksWithCreditPoints, fetchEmployeesWithCreditPoints, fetchEmployees, fetchRegularTasks, fetchDepartments, getUserInfo]);

  // Data loading
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Global event listener for task synchronization
  useEffect(() => {
    const handleGlobalRefresh = () => {
      console.log("🔄 Global performance refresh event received");
      loadData();
    };

    window.addEventListener("taskCreated", handleGlobalRefresh);
    window.addEventListener("taskUpdated", handleGlobalRefresh);

    return () => {
      window.removeEventListener("taskCreated", handleGlobalRefresh);
      window.removeEventListener("taskUpdated", handleGlobalRefresh);
    };
  }, [loadData]);

  // Show individual employee performance view if selected
  if (employeePerformanceView && selectedEmployee) {
    return <IndividualEmployeePerformance />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading performance data...</p>
          <p className="text-gray-400 text-sm">Fetching task completion data and calculating employee scores...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Performance Data</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all duration-300"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  // Enhanced Company-Wide Performance Chart Component with In-Progress Status
  const OverallPerformanceChart = ({ data, filters }) => {
    // Status normalization helper - EXACT match with Tasks.jsx
    const normalizeStatus = (status) => {
      if (!status) return 'pending';
      const statusLower = status.toLowerCase();

      // Map all completed variants to 'completed'
      if (statusLower === 'completed' ||
        statusLower === 'late-complete' ||
        statusLower === 're-complete' ||
        statusLower === 're-late-complete') {
        return 'completed';
      }

      // Map all in-progress variants to 'in-progress'
      if (statusLower === 'in-progress' ||
        statusLower === 'in-progress(overdue)' ||
        statusLower === 're-in-progress' ||
        statusLower === 're-in-progress(overdue)') {
        return 'in-progress';
      }

      // Separate reopened status category (NOT counted as pending)
      if (statusLower === 'reopened' ||
        statusLower === 'reopened(overdue)') {
        return 'reopened';
      }

      // Map pending (but NOT reopened)
      if (statusLower === 'pending') {
        return 'pending';
      }

      return statusLower;
    };

    if (!data || data.length === 0) {
      return (
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Performance Data</h3>
          <p className="text-gray-600">Apply filters to view company-wide performance metrics.</p>
        </div>
      );
    }

    // UPDATED: Recalculate employee data with status normalization
    const normalizedData = data.map(emp => {
      // If employee data already has task arrays, normalize them
      if (emp.tasks && Array.isArray(emp.tasks)) {
        const normalizedTasks = emp.tasks.map(task => ({
          ...task,
          normalizedStatus: normalizeStatus(task.status)
        }));

        const completedTasks = normalizedTasks.filter(t => t.normalizedStatus === 'completed').length;
        const inProgressTasks = normalizedTasks.filter(t => t.normalizedStatus === 'in-progress').length;
        const pendingTasks = normalizedTasks.filter(t => t.normalizedStatus === 'pending').length;
        const totalAssignedTasks = normalizedTasks.length;

        // Calculate late tasks (tasks with 'overdue' or 'late' in original status)
        const lateTasks = emp.tasks.filter(task => {
          const statusLower = (task.status || '').toLowerCase();
          return statusLower.includes('overdue') || statusLower.includes('late');
        }).length;

        // Calculate on-time tasks (completed without late/overdue)
        const onTimeTasks = emp.tasks.filter(task => {
          const statusLower = (task.status || '').toLowerCase();
          return (statusLower === 'completed' || statusLower === 're-complete') &&
            !statusLower.includes('late');
        }).length;

        const completionRate = totalAssignedTasks > 0 ? (completedTasks / totalAssignedTasks) * 100 : 0;
        const onTimeRate = completedTasks > 0 ? (onTimeTasks / completedTasks) * 100 : 0;

        return {
          ...emp,
          completedTasks,
          inProgressTasks,
          pendingTasks,
          lateTasks,
          onTimeTasks,
          totalAssignedTasks,
          completionRate,
          onTimeRate
        };
      }

      // Otherwise use existing calculated values (already normalized from backend)
      return emp;
    });

    // Calculate aggregate metrics from normalized data
    const totalCompletedTasks = normalizedData.reduce((sum, emp) => sum + (emp.completedTasks || 0), 0);
    const totalInProgressTasks = normalizedData.reduce((sum, emp) => sum + (emp.inProgressTasks || 0), 0);
    const totalPendingTasks = normalizedData.reduce((sum, emp) => sum + (emp.pendingTasks || 0), 0);
    const totalAssignedTasks = normalizedData.reduce((sum, emp) => sum + (emp.totalAssignedTasks || 0), 0);
    const avgCompletionRate = normalizedData.length > 0
      ? normalizedData.reduce((sum, emp) => sum + (emp.completionRate || 0), 0) / normalizedData.length
      : 0;
    const avgOnTimeRate = normalizedData.length > 0
      ? normalizedData.reduce((sum, emp) => sum + (emp.onTimeRate || 0), 0) / normalizedData.length
      : 0;
    const avgInProgressRate = normalizedData.length > 0
      ? normalizedData.reduce((sum, emp) => {
        const rate = emp.totalAssignedTasks > 0 ? ((emp.inProgressTasks || 0) / emp.totalAssignedTasks) * 100 : 0;
        return sum + rate;
      }, 0) / normalizedData.length
      : 0;
    const totalOnTimeTasks = normalizedData.reduce((sum, emp) => sum + (emp.onTimeTasks || 0), 0);
    const totalLateTasks = normalizedData.reduce((sum, emp) => sum + (emp.lateTasks || 0), 0);

    // Performance score distribution
    const excellent = normalizedData.filter(emp => emp.completionRate >= 80).length;
    const good = normalizedData.filter(emp => emp.completionRate >= 60 && emp.completionRate < 80).length;
    const average = normalizedData.filter(emp => emp.completionRate >= 40 && emp.completionRate < 60).length;
    const needsImprovement = normalizedData.filter(emp => emp.completionRate < 40).length;

    // Get top 10 performers for charts
    const topPerformers = [...normalizedData]
      .sort((a, b) => b.completionRate - a.completionRate)
      .slice(0, 10);

    const labels = topPerformers.map(emp => emp.name);

    // Chart 1: Performance Distribution - Pie Chart
    const pieData = {
      labels: ['Excellent (‰¥80%)', 'Good (60-79%)', 'Average (40-59%)', 'Needs Improvement (<40%)'],
      datasets: [{
        data: [excellent, good, average, needsImprovement],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)'
        ],
        borderColor: ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444'],
        borderWidth: 3
      }]
    };

    // Chart 2: Top Performers - Bar Chart (with in-progress rate)
    const barData = {
      labels,
      datasets: [{
        label: 'Completion Rate (%)',
        data: topPerformers.map(emp => emp.completionRate || 0),
        backgroundColor: 'rgba(99, 102, 241, 0.8)',
        borderColor: 'rgb(99, 102, 241)',
        borderWidth: 2,
        borderRadius: 8
      }]
    };

    // Chart 3: Task Status Breakdown - Stacked Bar (WITH IN-PROGRESS)
    const stackedData = {
      labels,
      datasets: [
        {
          label: 'Completed',
          data: topPerformers.map(emp => emp.completedTasks || 0),
          backgroundColor: 'rgba(34, 197, 94, 0.8)',
          borderColor: 'rgb(34, 197, 94)',
          borderWidth: 1
        },
        {
          label: 'In-Progress',
          data: topPerformers.map(emp => emp.inProgressTasks || 0),
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderColor: 'rgb(59, 130, 246)',
          borderWidth: 1
        },
        {
          label: 'Pending',
          data: topPerformers.map(emp => emp.pendingTasks || 0),
          backgroundColor: 'rgba(245, 158, 11, 0.8)',
          borderColor: 'rgb(245, 158, 11)',
          borderWidth: 1
        },
        {
          label: 'Late',
          data: topPerformers.map(emp => emp.lateTasks || 0),
          backgroundColor: 'rgba(239, 68, 68, 0.8)',
          borderColor: 'rgb(239, 68, 68)',
          borderWidth: 1
        }
      ]
    };

    // Chart 4: Multi-Status Rate Comparison - Line Chart (WITH IN-PROGRESS)
    const lineData = {
      labels,
      datasets: [
        {
          label: 'Completion Rate',
          data: topPerformers.map(emp => emp.completionRate || 0),
          borderColor: 'rgb(99, 102, 241)',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          borderWidth: 3,
          tension: 0.4,
          fill: true,
          pointRadius: 5,
          pointHoverRadius: 7
        },
        {
          label: 'On-Time Rate',
          data: topPerformers.map(emp => emp.onTimeRate || 0),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 3,
          tension: 0.4,
          fill: true,
          pointRadius: 5,
          pointHoverRadius: 7
        },
        {
          label: 'In-Progress Rate',
          data: topPerformers.map(emp => {
            const total = emp.totalAssignedTasks || 0;
            const inProgress = emp.inProgressTasks || 0;
            return total > 0 ? (inProgress / total) * 100 : 0;
          }),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 3,
          tension: 0.4,
          fill: true,
          pointRadius: 5,
          pointHoverRadius: 7
        }
      ]
    };

    // Chart 5: Multi-Metric Radar - Top 5 (WITH IN-PROGRESS)
    const top5 = topPerformers.slice(0, 5);
    const maxTasks = Math.max(...normalizedData.map(e => e.completedTasks || 0), 1);

    const radarData = {
      labels: ['Completion', 'On-Time', 'Task Volume', 'In-Progress', 'Efficiency'],
      datasets: top5.map((emp, idx) => {
        const inProgressRate = emp.totalAssignedTasks > 0 ? ((emp.inProgressTasks || 0) / emp.totalAssignedTasks) * 100 : 0;
        const efficiency = emp.completedTasks > 0 ? ((emp.onTimeTasks || 0) / emp.completedTasks) * 100 : 0;

        return {
          label: emp.name,
          data: [
            emp.completionRate || 0,
            emp.onTimeRate || 0,
            ((emp.completedTasks || 0) / maxTasks) * 100,
            inProgressRate,
            efficiency
          ],
          backgroundColor: [
            'rgba(99, 102, 241, 0.2)',
            'rgba(34, 197, 94, 0.2)',
            'rgba(245, 158, 11, 0.2)',
            'rgba(236, 72, 153, 0.2)',
            'rgba(139, 92, 246, 0.2)'
          ][idx],
          borderColor: [
            'rgb(99, 102, 241)',
            'rgb(34, 197, 94)',
            'rgb(245, 158, 11)',
            'rgb(236, 72, 153)',
            'rgb(139, 92, 246)'
          ][idx],
          borderWidth: 3,
          pointBackgroundColor: [
            'rgb(99, 102, 241)',
            'rgb(34, 197, 94)',
            'rgb(245, 158, 11)',
            'rgb(236, 72, 153)',
            'rgb(139, 92, 246)'
          ][idx],
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: [
            'rgb(99, 102, 241)',
            'rgb(34, 197, 94)',
            'rgb(245, 158, 11)',
            'rgb(236, 72, 153)',
            'rgb(139, 92, 246)'
          ][idx]
        };
      })
    };

    // Chart 6: Overall Task Distribution - Doughnut (WITH IN-PROGRESS)
    const doughnutData = {
      labels: ['Completed', 'In-Progress', 'Pending', 'Late'],
      datasets: [{
        data: [
          totalCompletedTasks,
          totalInProgressTasks,
          totalPendingTasks,
          totalLateTasks
        ],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)'
        ],
        borderColor: ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444'],
        borderWidth: 3,
        cutout: '70%'
      }]
    };

    // Chart 7: Work Progress Breakdown - Horizontal Stacked Bar (NEW)
    // Respect the global selectedStatus filter so the chart updates when
    // user chooses Completed / In-Progress / Pending in the filters panel.
    const baseProgressDatasets = [
      {
        label: 'Completed',
        data: topPerformers.map(emp => {
          const total = emp.totalAssignedTasks || 1;
          return ((emp.completedTasks || 0) / total) * 100;
        }),
        backgroundColor: 'rgba(34, 197, 94, 0.8)',
        borderColor: 'rgb(34, 197, 94)',
        borderWidth: 1,
      },
      {
        label: 'In-Progress',
        data: topPerformers.map(emp => {
          const total = emp.totalAssignedTasks || 1;
          return ((emp.inProgressTasks || 0) / total) * 100;
        }),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1,
      },
      {
        label: 'Pending',
        data: topPerformers.map(emp => {
          const total = emp.totalAssignedTasks || 1;
          return ((emp.pendingTasks || 0) / total) * 100;
        }),
        backgroundColor: 'rgba(245, 158, 11, 0.8)',
        borderColor: 'rgb(245, 158, 11)',
        borderWidth: 1,
      },
    ];

    const progressBreakdownData = {
      labels: topPerformers.map(emp => emp.name),
      datasets:
        filters?.status === 'completed'
          ? [baseProgressDatasets[0]]
          : filters?.status === 'in-progress'
            ? [baseProgressDatasets[1]]
            : filters?.status === 'pending'
              ? [baseProgressDatasets[2]]
              : baseProgressDatasets,
    };

    // Chart Options
    const pieOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            usePointStyle: true,
            padding: 15,
            font: { size: 11 }
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed;
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              return `${context.label}: ${value} (${percentage}%)`;
            }
          }
        }
      }
    };

    const barOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.95)',
          padding: 12,
          cornerRadius: 8
        }
      },
      scales: {
        x: {
          beginAtZero: true,
          max: 100,
          grid: { color: 'rgba(243, 244, 246, 0.8)' },
          ticks: { callback: val => val + '%', color: '#6b7280', font: { size: 11 } }
        },
        y: {
          grid: { display: false },
          ticks: { color: '#6b7280', font: { size: 11 } }
        }
      }
    };

    const stackedOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: { usePointStyle: true, padding: 15, font: { size: 12 } }
        }
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: { color: '#6b7280', maxRotation: 45, font: { size: 10 } }
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: 'rgba(243, 244, 246, 0.8)' },
          ticks: { color: '#6b7280', font: { size: 11 } }
        }
      }
    };

    const horizontalStackedOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: {
          position: 'top',
          labels: { usePointStyle: true, padding: 15, font: { size: 12 } }
        }
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          max: 100,
          grid: { color: 'rgba(243, 244, 246, 0.8)' },
          ticks: { callback: val => val + '%', color: '#6b7280', font: { size: 11 } }
        },
        y: {
          stacked: true,
          grid: { display: false },
          ticks: { color: '#6b7280', font: { size: 10 } }
        }
      }
    };

    const lineOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: { usePointStyle: true, padding: 15, font: { size: 12 } }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: '#6b7280', maxRotation: 45, font: { size: 10 } }
        },
        y: {
          beginAtZero: true,
          max: 100,
          grid: { color: 'rgba(243, 244, 246, 0.8)' },
          ticks: { callback: val => val + '%', color: '#6b7280', font: { size: 11 } }
        }
      }
    };

    const radarOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          beginAtZero: true,
          max: 100,
          angleLines: { color: 'rgba(156, 163, 175, 0.2)' },
          grid: { color: 'rgba(156, 163, 175, 0.2)' },
          pointLabels: {
            color: '#374151',
            font: { size: 11, weight: '500' }
          },
          ticks: {
            color: '#6b7280',
            backdropColor: 'transparent',
            stepSize: 20
          }
        }
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: { usePointStyle: true, padding: 15, font: { size: 11 } }
        }
      }
    };

    return (
      <div className="space-y-6">
        {/* Summary Cards - WITH IN-PROGRESS */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
            <div className="text-sm text-blue-600 font-medium mb-1">Total Employees</div>
            <div className="text-2xl font-bold text-blue-900">{normalizedData.length}</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
            <div className="text-sm text-green-600 font-medium mb-1">Completed</div>
            <div className="text-2xl font-bold text-green-900">{totalCompletedTasks}</div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
            <div className="text-sm text-blue-600 font-medium mb-1">In-Progress</div>
            <div className="text-2xl font-bold text-blue-900">{totalInProgressTasks}</div>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
            <div className="text-sm text-orange-600 font-medium mb-1">Avg Completion</div>
            <div className="text-2xl font-bold text-orange-900">{avgCompletionRate.toFixed(1)}%</div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
            <div className="text-sm text-purple-600 font-medium mb-1">Avg In-Progress</div>
            <div className="text-2xl font-bold text-purple-900">{avgInProgressRate.toFixed(1)}%</div>
          </div>
        </div>

        {/* Charts Grid - WITH IN-PROGRESS CALCULATIONS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">

          {/* Chart 2: Top Performers - Horizontal Bar */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">† Top Performers</h3>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">Bar</span>
            </div>
            <div className="h-72">
              <Bar data={barData} options={barOptions} />
            </div>
          </div>

          {/* Chart 4: Rate Comparison - Line WITH IN-PROGRESS */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">📈 Rate Comparison</h3>
              <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">Line</span>
            </div>
            <div className="h-72">
              <Line data={lineData} options={lineOptions} />
            </div>
          </div>

          {/* Chart 5: Multi-Metric Radar - Top 5 WITH IN-PROGRESS */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">🎯 Multi-Metric (Top 5)</h3>
              <span className="px-2 py-1 bg-pink-100 text-pink-700 text-xs font-medium rounded-full">Radar</span>
            </div>
            <div className="h-72">
              <Radar data={radarData} options={radarOptions} />
            </div>
          </div>



        </div>
      </div>
    );
  };


  // Main render
  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="w-full px-0" style={{ maxWidth: '100%', margin: 0, padding: 0 }}>

        <div className="relative mt-0 mx-0" style={{ marginBottom: '32px' }}>
          <div className="bg-white/95 backdrop-blur-xl rounded-none shadow-2xl border border-white/20 p-8" style={{ borderRadius: 0 }}>
            {/* Filter Header */}
            <div className={`flex items-center justify-between ${showFilters ? 'mb-8' : ''} transition-all duration-300`}>
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Data Filters</h3>
                  <p className="text-gray-600">Refine and explore performance data</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => exportData('csv', enhancedPerformanceData)}
                  className="group px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 rounded-xl transition-all duration-300 text-white text-sm font-medium flex items-center space-x-2 shadow-lg shadow-cyan-500/25 hover:-translate-y-0.5"
                >
                  <svg className="w-4 h-4 group-hover:rotate-12 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Export Data</span>
                </button>
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="group px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 rounded-xl transition-all duration-300 text-white text-sm font-medium flex items-center space-x-2 shadow-lg shadow-cyan-500/25 hover:-translate-y-0.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span>{showAnalytics ? 'Hide' : 'Show'} Analytics</span>
                </button>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-xl transition-all duration-300 text-sm font-medium flex items-center space-x-2 shadow-lg hover:-translate-y-0.5"
                >
                  <svg className={`w-4 h-4 transition-transform duration-300 ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                  <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                </button>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedDepartment('all');
                    setSelectedDepartmentEmployee('all');
                    setSelectedStatus('all');
                    setSelectedPriority('all');
                    setFilterBy('all');
                    setDateRange({ start: '', end: '' });
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white rounded-xl transition-all duration-300 text-sm font-medium flex items-center space-x-2 shadow-lg"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="hidden sm:inline">Reset All</span>
                </button>
              </div>
            </div>

            {/* Extracted Filter Grids with Animation Wrapper */}
            <div className={`transition-all duration-500 ease-in-out origin-top ${showFilters ? 'opacity-100 max-h-[2000px] transform scale-y-100 mt-8' : 'opacity-0 max-h-0 transform scale-y-95 overflow-hidden'}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
                {/* Search Filter */}
                <div className="space-y-3">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                    <div className="p-1.5 bg-blue-100 rounded-lg">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <span>Find Employee</span>
                  </label>
                  <div className="relative group">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by name..."
                      className="w-full px-4 py-3 pl-12 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300 text-sm placeholder-gray-400 group-hover:border-gray-300"
                    />
                    <svg
                      className="w-5 h-5 text-gray-400 absolute left-4 top-3.5 group-hover:text-blue-500 transition-colors"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                {/* Department Filter */}
                <div className="space-y-3">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                    <div className="p-1.5 bg-purple-100 rounded-lg">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <span>Department</span>
                  </label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => {
                      setSelectedDepartment(e.target.value);
                      setSelectedDepartmentEmployee('all');
                    }}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-100 transition-all duration-300 text-sm appearance-none hover:border-gray-300"
                  >
                    <option value="all" className="text-gray-500">All Departments</option>
                    {departments.map((dept) => (
                      <option key={dept._id || dept.id} value={dept._id || dept.id} className="text-gray-900">
                        {dept.name || dept.departmentName || 'Unnamed Department'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Department Employee Filter */}
                <div className="space-y-3">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                    <div className="p-1.5 bg-green-100 rounded-lg">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <span>
                      Team Member
                      {selectedDepartment !== 'all' && (
                        <span className="ml-1 text-green-600 font-medium">({departmentEmployees.length})</span>
                      )}
                    </span>
                  </label>
                  <select
                    value={selectedDepartmentEmployee}
                    onChange={(e) => setSelectedDepartmentEmployee(e.target.value)}
                    disabled={selectedDepartment === 'all'}
                    className={`w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-4 transition-all duration-300 text-sm appearance-none ${selectedDepartment === 'all'
                      ? 'bg-gray-100 border-2 border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-50 border-2 border-gray-200 focus:border-green-500 focus:ring-green-100 hover:border-gray-300 text-gray-900'
                      }`}
                  >
                    <option value="all" className="text-gray-500">
                      {selectedDepartment === 'all' ? 'Select Department First' : 'All Team Members'}
                    </option>
                    {departmentEmployees.filter(emp => emp.isActive !== false).map((emp) => (
                      <option key={emp._id || emp.id} value={emp._id || emp.id} className="text-gray-900">
                        {emp.name || emp.teamMemberName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unnamed Employee'}
                      </option>
                    ))}
                    {selectedDepartment !== 'all' && departmentEmployees.length === 0 && (
                      <option disabled className="text-gray-400">No team members found</option>
                    )}
                  </select>
                </div>

                {/* Status Filter */}
                <div className="space-y-3">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                    <div className="p-1.5 bg-yellow-100 rounded-lg">
                      <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span>Task Status</span>
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100 transition-all duration-300 text-sm appearance-none hover:border-gray-300"
                  >
                    <option value="all" className="text-gray-500">All Status</option>
                    <option value="completed" className="text-green-600">✅ Completed</option>
                    <option value="in-progress" className="text-blue-600">🔄 In Progress</option>
                    <option value="pending" className="text-orange-600"> Pending</option>
                  </select>
                </div>

                {/* Priority Filter */}
                <div className="space-y-3">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                    <div className="p-1.5 bg-red-100 rounded-lg">
                      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                    </div>
                    <span>Priority Level</span>
                  </label>
                  <select
                    value={selectedPriority}
                    onChange={(e) => setSelectedPriority(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-sm appearance-none hover:border-gray-300"
                  >
                    <option value="all" className="text-gray-500">All Priorities</option>
                    <option value="high" className="text-red-600">🔍 High Priority</option>
                    <option value="medium" className="text-yellow-600">Ÿ¡ Medium Priority</option>
                    <option value="low" className="text-green-600">🟢 Low Priority</option>
                  </select>
                </div>
              </div>

              {/* Advanced Filters Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Performance Category */}
                <div className="space-y-3">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                    <div className="p-1.5 bg-indigo-100 rounded-lg">
                      <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <span>Performance Tier</span>
                  </label>
                  <select
                    value={filterBy}
                    onChange={(e) => setFilterBy(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-300 text-sm appearance-none hover:border-gray-300"
                  >
                    <option value="all" className="text-gray-500">All Employees</option>
                    <option value="topPerformers" className="text-green-600">† Elite Performers (70+)</option>
                    <option value="needsImprovement" className="text-orange-600">📈 Growth Opportunity (&lt;50)</option>
                    <option value="noActivity" className="text-gray-600"> Inactive Members</option>
                  </select>
                </div>

                {/* Date Range Start */}
                <div className="space-y-3">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                    <div className="p-1.5 bg-cyan-100 rounded-lg">
                      <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span>Start Date</span>
                  </label>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100 transition-all duration-300 text-sm hover:border-gray-300"
                  />
                </div>

                {/* Date Range End */}
                <div className="space-y-3">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                    <div className="p-1.5 bg-cyan-100 rounded-lg">
                      <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span>End Date</span>
                  </label>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100 transition-all duration-300 text-sm hover:border-gray-300"
                  />
                </div>

                {/* Quick Export */}
                <div className="space-y-3">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 opacity-0">
                    <span>Actions</span>
                  </label>
                  <button
                    onClick={() => exportData('csv', enhancedPerformanceData)}
                    className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-xl transition-all duration-300 text-sm font-medium flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                    <span>Quick Export</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Active Filters Display */}
            {(searchTerm || selectedDepartment !== 'all' || selectedDepartmentEmployee !== 'all' ||
              selectedStatus !== 'all' || selectedPriority !== 'all' || filterBy !== 'all' ||
              dateRange.start || dateRange.end) && (
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-gray-700">Active Filters</span>
                      <span className="text-sm text-blue-600 ml-2 font-medium">{enhancedPerformanceData.length} results</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {searchTerm && (
                      <span className="px-4 py-2 bg-blue-50 text-blue-700 rounded-xl text-sm font-medium flex items-center space-x-2 border border-blue-200 shadow-sm">
                        <span>Search: "{searchTerm}"</span>
                        <button onClick={() => setSearchTerm('')} className="ml-1 hover:text-blue-900 transition-colors p-1 rounded-full hover:bg-blue-200">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    )}

                    {selectedDepartment !== 'all' && (
                      <span className="px-4 py-2 bg-purple-50 text-purple-700 rounded-xl text-sm font-medium flex items-center space-x-2 border border-purple-200 shadow-sm">
                        <span>Dept: {departments.find(d => (d._id || d.id) === selectedDepartment)?.name || selectedDepartment}</span>
                        <button onClick={() => {
                          setSelectedDepartment('all');
                          setSelectedDepartmentEmployee('all');
                        }} className="ml-1 hover:text-purple-900 transition-colors p-1 rounded-full hover:bg-purple-200">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    )}

                    {selectedDepartmentEmployee !== 'all' && (
                      <span className="px-4 py-2 bg-green-50 text-green-700 rounded-xl text-sm font-medium flex items-center space-x-2 border border-green-200 shadow-sm">
                        <span>Member: {(() => {
                          const emp = employees.find(e => (e._id || e.id) === selectedDepartmentEmployee);
                          return emp ? (emp.name || emp.teamMemberName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unnamed Employee') : selectedDepartmentEmployee;
                        })()}</span>
                        <button onClick={() => setSelectedDepartmentEmployee('all')} className="ml-1 hover:text-green-900 transition-colors p-1 rounded-full hover:bg-green-200">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    )}


                    {selectedStatus !== 'all' && (
                      <span className="px-4 py-2 bg-yellow-50 text-yellow-700 rounded-xl text-sm font-medium flex items-center space-x-2 border border-yellow-200 shadow-sm">
                        <span>Status: {selectedStatus}</span>
                        <button onClick={() => setSelectedStatus('all')} className="ml-1 hover:text-yellow-900 transition-colors p-1 rounded-full hover:bg-yellow-200">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    )}

                    {selectedPriority !== 'all' && (
                      <span className="px-4 py-2 bg-red-50 text-red-700 rounded-xl text-sm font-medium flex items-center space-x-2 border border-red-200 shadow-sm">
                        <span>Priority: {selectedPriority}</span>
                        <button onClick={() => setSelectedPriority('all')} className="ml-1 hover:text-red-900 transition-colors p-1 rounded-full hover:bg-red-200">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    )}

                    {filterBy !== 'all' && (
                      <span className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-xl text-sm font-medium flex items-center space-x-2 border border-indigo-200 shadow-sm">
                        <span>Tier: {filterBy.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
                        <button onClick={() => setFilterBy('all')} className="ml-1 hover:text-indigo-900 transition-colors p-1 rounded-full hover:bg-indigo-200">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    )}

                    {(dateRange.start || dateRange.end) && (
                      <span className="px-4 py-2 bg-cyan-50 text-cyan-700 rounded-xl text-sm font-medium flex items-center space-x-2 border border-cyan-200 shadow-sm">
                        <span>Date: {dateRange.start || 'Start'} †{dateRange.end || 'End'}</span>
                        <button onClick={() => setDateRange({ start: '', end: '' })} className="ml-1 hover:text-cyan-900 transition-colors p-1 rounded-full hover:bg-cyan-200">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className={`grid ${showAnalytics ? 'grid-cols-1 lg:grid-cols-4' : 'grid-cols-1'} gap-8`}>
          {/* Analytics Sidebar */}
          {showAnalytics && (
            <div className="lg:col-span-1">
              <AnalyticsSidebar />
            </div>
          )}

          {/* Performance Display */}
          <div className={showAnalytics ? 'lg:col-span-3' : 'col-span-1'}>
            {viewMode === 'cards' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {enhancedPerformanceData.map((employee, index) => (
                  <PerformanceCard key={employee.id} employee={employee} index={index} />
                ))}
                {enhancedPerformanceData.length === 0 && (
                  <div className="col-span-full text-center py-12">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Performance Data Available</h3>
                    <p className="text-gray-600">Complete some tasks to see performance metrics here.</p>
                  </div>
                )}
              </div>
            ) : viewMode === 'chart' ? (
              <div>
                {enhancedPerformanceData && enhancedPerformanceData.length > 0 ? (
                  chartData
                ) : (
                  <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="text-center py-12">
                      <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Chart Data Available</h3>
                      <p className="text-gray-600">Complete some tasks to see performance charts here.</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Table View */
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Employee Performance Rankings</h2>
                      <p className="text-gray-600 mt-1">Based on task completion and timing performance</p>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left">
                          <button
                            onClick={() => handleSort('name')}
                            className="flex items-center space-x-1 font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                          >
                            <span>Employee</span>
                            {sortBy === 'name' && (
                              <svg className={`w-4 h-4 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                              </svg>
                            )}
                          </button>
                        </th>
                        <th className="px-6 py-4 text-center">
                          <button
                            onClick={() => handleSort('completedTasks')}
                            className="flex items-center space-x-1 font-semibold text-gray-900 hover:text-blue-600 transition-colors mx-auto"
                          >
                            <span>Completed Tasks</span>
                            {sortBy === 'completedTasks' && (
                              <svg className={`w-4 h-4 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                              </svg>
                            )}
                          </button>
                        </th>
                        <th className="px-6 py-4 text-center">
                          <button
                            onClick={() => handleSort('completedTasks')}
                            className="flex items-center space-x-1 font-semibold text-gray-900 hover:text-blue-600 transition-colors mx-auto"
                          >
                            <span>Task Completion</span>
                            {sortBy === 'completedTasks' && (
                              <svg className={`w-4 h-4 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                              </svg>
                            )}
                          </button>
                        </th>
                        <th className="px-6 py-4 text-center">
                          <button
                            onClick={() => handleSort('completionRate')}
                            className="flex items-center space-x-1 font-semibold text-gray-900 hover:text-blue-600 transition-colors mx-auto"
                          >
                            <span>Completion Rate</span>
                            {sortBy === 'completionRate' && (
                              <svg className={`w-4 h-4 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                              </svg>
                            )}
                          </button>
                        </th>
                        <th className="px-6 py-4 text-center">
                          <button
                            onClick={() => handleSort('onTimeRate')}
                            className="flex items-center space-x-1 font-semibold text-gray-900 hover:text-blue-600 transition-colors mx-auto"
                          >
                            <span>On-Time Rate</span>
                            {sortBy === 'onTimeRate' && (
                              <svg className={`w-4 h-4 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                              </svg>
                            )}
                          </button>
                        </th>
                        <th className="px-6 py-4 text-center">
                          <button
                            onClick={() => handleSort('performanceScore')}
                            className="flex items-center space-x-1 font-semibold text-gray-900 hover:text-blue-600 transition-colors mx-auto"
                          >
                            <span>Performance Score</span>
                            {sortBy === 'performanceScore' && (
                              <svg className={`w-4 h-4 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                              </svg>
                            )}
                          </button>
                        </th>
                        <th className="px-6 py-4 text-center font-semibold text-gray-900 hover:text-blue-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {enhancedPerformanceData.map((employee, index) => (
                        <EmployeeRow
                          key={employee.id}
                          employee={employee}
                          index={index}
                          getPerformanceBadge={getPerformanceBadge}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                {enhancedPerformanceData.length === 0 && (
                  <div className="text-center py-12">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Performance Data Available</h3>
                    <p className="text-gray-600">Complete some tasks to see performance metrics here.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Overall Company Performance Section - NEW */}
        <div className="mb-8" style={{ marginTop: '32px' }}>
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Company-Wide Performance</h2>
                <p className="text-gray-600 mt-1">Aggregated metrics across all filtered employees</p>
              </div>
              <div className="flex items-center space-x-2">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span className="text-sm font-medium text-gray-700">Live Analytics</span>
              </div>
            </div>

            <OverallPerformanceChart
              data={enhancedPerformanceData}
              filters={{
                department: selectedDepartment,
                employee: selectedDepartmentEmployee,
                employeeName: selectedDepartmentEmployee !== 'all'
                  ? employees.find(e => e.id === selectedDepartmentEmployee)?.name
                  : 'All',
                status: selectedStatus
              }}
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default EmployeePerformance;

